import { TrlProjectionTargetDto } from '@@types/api.types';

const Z_SCORE_P10 = 1.2815515655446004;

export type TrlProjectionStep = {
  deterministicDrivers: string[];
  fromTrl: number;
  label: string;
  dominantDriversNarrative?: string;
  p10Months: number;
  p50Months: number;
  p90Months: number;
  toTrl: number;
};

export type TrlCumulativePoint = {
  month: number;
  p10: number;
  p50: number;
  p90: number;
};

export type TrlCumulativeTarget = {
  p10Months: number;
  p50Months: number;
  p90Months: number;
  targetTrl: number;
};

export type TrlCumulativeProjection = {
  maxMonths: number;
  points: TrlCumulativePoint[];
  targets: TrlCumulativeTarget[];
};

export type TrlDensityPoint = {
  density: number;
  month: number;
};

function logNormalPdf(params: { month: number; mu: number; sigma: number }): number {
  const { month, mu, sigma } = params;

  if (month <= 0 || sigma <= 0) return 0;

  const logValue = Math.log(month);
  const exponent = -Math.pow(logValue - mu, 2) / (2 * sigma * sigma);
  const denominator = month * sigma * Math.sqrt(2 * Math.PI);

  return Math.exp(exponent) / denominator;
}

export function buildLogNormalDensity(params: {
  p10Months: number;
  p50Months: number;
  p90Months: number;
}): TrlDensityPoint[] {
  const { p10Months, p50Months, p90Months } = params;

  const sigma = (Math.log(p90Months) - Math.log(p10Months)) / (2 * Z_SCORE_P10);
  const mu = Math.log(p50Months);
  const maxMonths = Math.ceil(p90Months * 2);
  const step = Math.max(0.25, maxMonths / 120);

  const points: TrlDensityPoint[] = [];

  for (let month = 0; month <= maxMonths; month += step) {
    const roundedMonth = Number(month.toFixed(2));
    const density = logNormalPdf({ month: roundedMonth, mu, sigma });

    points.push({ density, month: roundedMonth });
  }

  return points;
}

export function buildProjectionStepsFromTargets(params: {
  baseTrl: number;
  targets: TrlProjectionTargetDto[];
}): TrlProjectionStep[] {
  const { baseTrl, targets } = params;

  let previousP10Months = 0;
  let previousP50Months = 0;
  let previousP90Months = 0;
  let previousTargetTrl = baseTrl;

  return targets.map((target) => {
    const step: TrlProjectionStep = {
      deterministicDrivers: target.deterministicDrivers,
      dominantDriversNarrative: target.dominantDriversNarrative,
      fromTrl: previousTargetTrl,
      label: target.label,
      p10Months: Number((target.p10Months - previousP10Months).toFixed(1)),
      p50Months: Number((target.p50Months - previousP50Months).toFixed(1)),
      p90Months: Number((target.p90Months - previousP90Months).toFixed(1)),
      toTrl: target.targetTrl,
    };

    previousP10Months = target.p10Months;
    previousP50Months = target.p50Months;
    previousP90Months = target.p90Months;
    previousTargetTrl = target.targetTrl;

    return step;
  });
}
